import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon } from '@/ui/icons/Icon';
import { cx } from './cx';
import { Tooltip, type TooltipPlacement } from './Tooltip';
import styles from './IconButton.module.css';

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  readonly icon: string;
  /** Accessible name; also the tooltip text. Icon-only buttons must always have one. */
  readonly label: string;
  /** Extra tooltip text, e.g. why the button is disabled. */
  readonly hint?: ReactNode;
  readonly pressed?: boolean;
  readonly placement?: TooltipPlacement;
  readonly iconSize?: number;
}

export function IconButton({
  icon,
  label,
  hint,
  pressed,
  placement = 'bottom',
  iconSize = 20,
  type = 'button',
  className,
  ...rest
}: IconButtonProps) {
  return (
    <Tooltip
      placement={placement}
      content={
        hint === undefined ? (
          label
        ) : (
          <>
            <strong>{label}</strong> — {hint}
          </>
        )
      }
    >
      <button
        type={type}
        aria-label={label}
        {...(pressed !== undefined ? { 'aria-pressed': pressed } : {})}
        className={cx(styles.iconButton, pressed === true && styles.pressed, className)}
        {...rest}
      >
        <Icon name={icon} size={iconSize} />
      </button>
    </Tooltip>
  );
}
